import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useState, useRef } from "react";
import api from "@/lib/api";
import { inr, initials } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { StatusBadge } from "@/components/pawkt/StatusBadge";
import { CustomerCreateModal } from "@/components/pawkt/CustomerCreateModal";
import { Button } from "@/components/ui/button";
import { Plus, Search, Users, Phone, BarChart3 } from "lucide-react";
import { ArrivalDialog } from "@/components/visits/ArrivalDialog";
import { PhoneIssuesButton } from "@/components/pawkt/PhoneIssuesButton";
import { CustomerIntelligence } from "@/components/pawkt/CustomerIntelligence";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { useRangeParams, apiQueryString, DrillDownBanner } from "@/lib/drilldown";

export default function Customers() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [ctype, setCtype] = useState("");
  const [arrival, setArrival] = useState(false);
  const [open, setOpen] = useState(false);
  const listRef = useRef(null);
  const openList = () => { listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); listRef.current?.querySelector("input")?.focus({ preventScroll: true }); };
  const range = useRangeParams();
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["customers", search, ctype, range.period, range.start, range.end],
    queryFn: async () => {
      const params = { search, limit: 100 };
      if (ctype) params.customer_type = ctype;
      if (range.period) {
        params.period = range.period;
        if (range.period === "custom") { params.start = range.start; params.end = range.end; }
      }
      return (await api.get(`/customers${apiQueryString(params)}`)).data;
    },
  });

  return (
    <div>
      <ArrivalDialog open={arrival} onClose={() => setArrival(false)} />
      <PageHeader title="Customers" subtitle="Your pet parents and their relationships">
        {hasPerm(user, "settings.edit") && <PhoneIssuesButton />}
        <Button variant="outline" onClick={() => setArrival(true)} data-testid="customers-record-arrival-btn" className="gap-1.5">Record arrival</Button>
        <Button variant="outline" onClick={() => navigate("/customers/intelligence")} data-testid="customer-intel-btn" className="gap-1.5">
          <BarChart3 className="h-4 w-4" /> Customer Intelligence
        </Button>
        <Button onClick={() => setOpen(true)} data-testid="add-customer-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
          <Plus className="h-4 w-4" /> Add Customer
        </Button>
      </PageHeader>

      <CustomerIntelligence onTotalClick={openList} />
      <DrillDownBanner metric="new_customers" count={data?.total} />

      <div ref={listRef} className="mb-4 flex scroll-mt-20 flex-wrap items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="customer-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, mobile, email…"
            className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10" />
        </div>
        <select value={ctype} onChange={(e) => setCtype(e.target.value)} data-testid="customer-type-filter" aria-label="Filter by customer status"
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500">
          <option value="">All statuses</option><option value="new">New</option><option value="regular">Regular</option><option value="vip">VIP</option>
        </select>
        {data && <span className="text-xs text-slate-500" data-testid="customer-list-count">{data.total.toLocaleString("en-IN")} customer{data.total === 1 ? "" : "s"}{data.total > data.items.length ? ` · showing ${data.items.length}` : ""}</span>}
      </div>

      {isLoading ? <Loading /> : (data?.items?.length ? (
        <>
        <div className="md:hidden space-y-2.5" data-testid="customers-cards">
          {data.items.map((c) => (
            <button key={c.id} onClick={() => navigate(`/customers/${c.id}`)} data-testid={`customer-card-${c.id}`}
              className="w-full text-left rounded-[18px] border border-pawkt-border bg-white p-4 active:scale-[0.99] transition-transform">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold shrink-0">{initials(c.name)}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><Phone className="h-3 w-3" />{c.mobile}</div>
                </div>
                <StatusBadge status={c.customer_type} />
              </div>
              <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                <span>{c.pet_count} pet{c.pet_count === 1 ? "" : "s"}{c.city ? ` · ${c.city}` : ""}</span>
                <span className="font-bold text-slate-900 text-sm tabular">{inr(c.total_spend)}</span>
              </div>
            </button>
          ))}
        </div>
        <div className="hidden md:block rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-medium">Customer</th>
                <th className="px-4 py-3 font-medium hidden sm:table-cell">Mobile</th>
                <th className="px-4 py-3 font-medium hidden md:table-cell">City</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium text-right">Pets</th>
                <th className="px-4 py-3 font-medium text-right">Total spend</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((c) => (
                <tr key={c.id} onClick={() => navigate(`/customers/${c.id}`)} data-testid={`customer-row-${c.id}`}
                  className="cursor-pointer hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">{initials(c.name)}</div>
                      <div>
                        <div className="font-medium text-slate-900">{c.name}</div>
                        <div className="text-xs text-slate-400 sm:hidden flex items-center gap-1"><Phone className="h-3 w-3" />{c.mobile}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 hidden sm:table-cell tabular">{c.mobile}</td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{c.city || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={c.customer_type} /></td>
                  <td className="px-4 py-3 text-right tabular">{c.pet_count}</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900 tabular">{inr(c.total_spend)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      ) : <Empty icon={Users} title="No customers yet" subtitle="Add your first pet parent to get started." action={<Button onClick={() => setOpen(true)} className="bg-slate-900 text-white">Add Customer</Button>} />)}

      <CustomerCreateModal open={open} onOpenChange={setOpen} />
    </div>
  );
}
